import { VehicleModelWithInsurancesForD2CDto } from '@orcar/common';
import {
  CAR_FUEL,
  CAR_OPTION,
  CheckboxFilter,
  ColoredSurfaceWithIconButton,
  filterInsuranceAtom,
  filterOptionAtom,
  FilterOptionsType,
  IconV2,
  SliderFilter,
  useRecoil,
} from '@orcar/common-d2c';
import { useEffect, useMemo, useState } from 'react';
import { INSURANCE_BUTTONS } from '@/constants/insurance';
import { CAR_TYPE } from '@/constants/vehicle-model';
import { getDefaultFilterOptions } from '@/utils/filterOptions';
import { getFilteredSearchResult } from '@/utils/vehicle-model';
import InsuranceInfoModal from './InsuranceInfoModal';

type SearchFiltersProps = {
  vehicleModelList?: VehicleModelWithInsurancesForD2CDto[];
};

const SearchFilters = ({ vehicleModelList }: SearchFiltersProps) => {
  const {
    recoilState: [recoilFilterOption, setRecoilFilterOption],
  } = useRecoil(filterOptionAtom);
  const {
    recoilState: [recoilInsurance, setRecoilInsurance],
  } = useRecoil(filterInsuranceAtom);

  const [insurance, setInsurance] = useState<string[]>([]);
  const [minPrice, setMinPrice] = useState<number>(0);
  const [maxPrice, setMaxPrice] = useState<number>(0);
  const [minYear, setMinYear] = useState<number>(0);
  const [maxYear, setMaxYear] = useState<number>(0);
  const [carTypeList, setCarTypeList] = useState<string[]>([]);
  const [carFuelList, setCarFuelList] = useState<string[]>([]);
  const [optionList, setOptionList] = useState<string[]>([]);
  const [openInsuranceModal, setOpenInsuranceModal] = useState(false);

  const localFilterResult = getFilteredSearchResult(
    vehicleModelList,
    {
      minPrice,
      maxPrice,
      minYear,
      maxYear,
      carTypeList,
      carFuelList,
      optionList,
    },
    insurance,
  );

  const defaultFilterOptions = useMemo(
    () => getDefaultFilterOptions(vehicleModelList),
    [vehicleModelList],
  );

  useEffect(() => {
    const {
      minPrice,
      maxPrice,
      minYear,
      maxYear,
      carTypeList,
      carFuelList,
      optionList,
    } = recoilFilterOption;

    setMinPrice(minPrice || defaultFilterOptions.minPrice);
    setMaxPrice(maxPrice || defaultFilterOptions.maxPrice);
    setMinYear(minYear || defaultFilterOptions.minYear);
    setMaxYear(maxYear || defaultFilterOptions.maxYear);
    setCarTypeList(carTypeList);
    setOptionList(optionList);
    setCarFuelList(carFuelList);
  }, [recoilFilterOption, defaultFilterOptions]);

  useEffect(() => {
    setInsurance(recoilInsurance);
  }, [recoilInsurance]);

  const handleReset = () => {
    setMinPrice(defaultFilterOptions.minPrice);
    setMaxPrice(defaultFilterOptions.maxPrice);
    setMinYear(defaultFilterOptions.minYear);
    setMaxYear(defaultFilterOptions.maxYear);
    setCarTypeList([]);
    setCarFuelList([]);
    setOptionList([]);
    setInsurance([]);
  };

  const handleApplyFilter = (
    {
      minPrice,
      maxPrice,
      minYear,
      maxYear,
      carTypeList,
      carFuelList,
      optionList,
    }: FilterOptionsType,
    insurance: string[],
  ) => {
    window.scrollTo({ top: 0 });

    setRecoilFilterOption({
      minPrice: minPrice === defaultFilterOptions.minPrice ? null : minPrice,
      maxPrice: maxPrice === defaultFilterOptions.maxPrice ? null : maxPrice,
      minYear: minYear === defaultFilterOptions.minYear ? null : minYear,
      maxYear: maxYear === defaultFilterOptions.maxYear ? null : maxYear,
      carTypeList,
      carFuelList,
      optionList,
    });
    setRecoilInsurance(insurance);
  };

  return (
    <>
      <div className='scrollbar-thin relative flex h-[calc(100vh-349px)] w-[292px] flex-col gap-6 overflow-hidden overflow-y-scroll rounded-m border-x border-t border-grey-010  bg-grey-000'>
        <div className='sticky  bg-grey-000 px-5 pt-8'>
          <p className='sub1 text-grey-090'>검색필터</p>
        </div>
        <div className='flex flex-col gap-9 px-5'>
          <div className='flex flex-col gap-4'>
            <div className='flex '>
              <p className='sub3 text-grey-070'>보험</p>
              <div
                className='size-6 cursor-pointer'
                onClick={() => setOpenInsuranceModal(true)}
              >
                <IconV2 icon='info' />
              </div>
            </div>
            <div className='flex flex-col gap-5'>
              <CheckboxFilter
                checkboxItemList={INSURANCE_BUTTONS}
                checkedList={insurance}
                setCheckedList={(value) => setInsurance(value)}
                itemCountPerRow={1}
              />
            </div>
          </div>

          <div className='h-px bg-grey-010 ' />

          <div className='flex flex-col gap-4'>
            <p className='sub3 text-grey-070'>금액</p>
            <div className='flex flex-col gap-5'>
              <SliderFilter
                value={[minPrice, maxPrice]}
                min={defaultFilterOptions.minPrice}
                max={defaultFilterOptions.maxPrice}
                rangeText={`${minPrice.toLocaleString()}원 ~ ${maxPrice.toLocaleString()}원`}
                step={1000}
                setValue={(value) => {
                  const [min, max] = value;
                  setMinPrice(min);
                  setMaxPrice(max);
                }}
              />
            </div>
          </div>

          <div className='h-px bg-grey-010 ' />

          <div className='flex flex-col gap-4'>
            <p className='sub3 text-grey-070'>차종</p>
            <CheckboxFilter
              checkboxItemList={CAR_TYPE}
              checkedList={carTypeList}
              setCheckedList={(value) => setCarTypeList(value)}
            />
          </div>

          <div className='h-px bg-grey-010 ' />

          <div className='flex flex-col gap-4'>
            <p className='sub3 text-grey-070'>연료</p>
            <CheckboxFilter
              checkboxItemList={CAR_FUEL}
              checkedList={carFuelList}
              setCheckedList={(value) => setCarFuelList(value)}
            />
          </div>

          <div className='h-px bg-grey-010 ' />

          <div className='flex flex-col gap-4'>
            <p className='sub3 text-grey-070'>연식</p>
            <div className='flex flex-col gap-5'>
              <SliderFilter
                value={[minYear, maxYear]}
                min={defaultFilterOptions.minYear}
                max={defaultFilterOptions.maxYear}
                step={1}
                rangeText={`${minYear}년 ~ ${maxYear}년`}
                setValue={(value) => {
                  const [min, max] = value;
                  setMinYear(min);
                  setMaxYear(max);
                }}
              />
            </div>
          </div>

          <div className='h-px bg-grey-010 ' />

          <div className='flex  flex-col gap-4'>
            <p className='sub3 text-grey-070'>추가옵션</p>
            <CheckboxFilter
              checkboxItemList={CAR_OPTION}
              checkedList={optionList}
              setCheckedList={(value) => setOptionList(value)}
              itemCountPerRow={2}
            />
          </div>
        </div>
      </div>
      <div className='bottom-0 flex justify-between gap-2 rounded-b-m border-x border-b border-grey-010 bg-grey-000 px-5 pb-8 pt-6'>
        <button
          className='flex w-[61px] items-center justify-center text-nowrap rounded-s border border-solid border-primary-050 px-8 py-2 text-primary-050'
          onClick={handleReset}
        >
          초기화
        </button>
        <ColoredSurfaceWithIconButton
          type='default'
          title={`${localFilterResult?.length || 0}개의 차량 보기`}
          onClick={() =>
            handleApplyFilter(
              {
                minPrice,
                maxPrice,
                minYear,
                maxYear,
                carTypeList,
                carFuelList,
                optionList,
              },
              insurance,
            )
          }
        />
      </div>
      {openInsuranceModal && (
        <InsuranceInfoModal onClose={() => setOpenInsuranceModal(false)} />
      )}
    </>
  );
};

export default SearchFilters;
